/**
 * Starting arrangements, ported from `reference/deno-2023/common/cells.ts`.
 *
 * Cells are named by `"q,r,s"` coordinate key rather than by index, exactly as they were
 * in the original, which is what makes these definitions independent of the board's
 * internal cell ordering. See RULES.md §9.
 *
 * The 2023 build also had a `'tutorial'` formation — an empty board that a privileged
 * bot mutated mid-game over the wire. The tutorial is now a client-side script against a
 * local engine, so that formation and the server code path it needed are both gone.
 */

import { getBoard } from './board.ts';
import { type FormationName, type Piece, type Variant } from './types.ts';

/** A starting arrangement: piece by `"q,r,s"` coordinate key. */
export type Formation = Readonly<Record<string, Piece>>;

const king = (player: 0 | 1): Piece => ({ player, type: 'king' });

export class InvalidVariantError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidVariantError';
  }
}

interface FormationDefn {
  /** Smallest board this arrangement fits on. */
  readonly minRadius: number;
  readonly kingsPerPlayer: number;
  readonly build: (radius: number) => Formation;
}

const DEFNS: Readonly<Record<FormationName, FormationDefn>> = {
  monarchy: {
    minRadius: 2,
    kingsPerPlayer: 1,
    build: () => ({
      '1,-2,1': king(0),
      '-1,2,-1': king(1),
    }),
  },

  diarchy: {
    minRadius: 3,
    kingsPerPlayer: 2,
    build: () => ({
      '1,-3,2': king(0),
      '2,-3,1': king(0),
      '-1,3,-2': king(1),
      '-2,3,-1': king(1),
    }),
  },

  triarchy: {
    minRadius: 3,
    kingsPerPlayer: 3,
    build: () => ({
      '1,-2,1': king(0),
      '1,-3,2': king(0),
      '2,-3,1': king(0),
      '-1,2,-1': king(1),
      '-1,3,-2': king(1),
      '-2,3,-1': king(1),
    }),
  },

  // The six corners of the board, alternating owners.
  colonies: {
    minRadius: 3,
    kingsPerPlayer: 3,
    build: (r) => ({
      [`0,-${r},${r}`]: king(0),
      [`${r},-${r},0`]: king(1),
      [`${r},0,-${r}`]: king(0),
      [`0,${r},-${r}`]: king(1),
      [`-${r},${r},0`]: king(0),
      [`-${r},0,${r}`]: king(1),
    }),
  },
};

export const formationInfo = (name: FormationName): FormationDefn => {
  const defn = DEFNS[name];
  if (!defn) throw new InvalidVariantError(`Unknown formation ${JSON.stringify(name)}`);
  return defn;
};

/** Whether a variant describes a board this formation actually fits on. */
export const isVariantValid = (variant: Variant): boolean => {
  const defn = DEFNS[variant.formation];
  return defn !== undefined && variant.radius >= defn.minRadius;
};

export const assertVariantValid = (variant: Variant): void => {
  const defn = formationInfo(variant.formation);
  if (variant.radius < defn.minRadius) {
    throw new InvalidVariantError(
      `Formation ${variant.formation} needs radius >= ${defn.minRadius}, got ${variant.radius}`,
    );
  }
};

/**
 * The starting arrangement for a variant, as a coordinate-keyed map.
 *
 * Built once per call — unlike the 2023 `makeCells`, which rebuilt the whole formation
 * object once for every cell on the board because the call sat inside a `.map`.
 */
export function getFormation(variant: Variant): Formation {
  assertVariantValid(variant);
  const formation = formationInfo(variant.formation).build(variant.radius);

  const board = getBoard(variant.radius);
  for (const k of Object.keys(formation)) {
    if (board.indexOf(k) === undefined) {
      throw new InvalidVariantError(
        `Formation ${variant.formation} places a piece at ${k}, which is off a radius-${variant.radius} board`,
      );
    }
  }
  return formation;
}

/** The starting arrangement as a per-cell array, indexed by board cell index. */
export function makeCells(variant: Variant): (Piece | null)[] {
  const board = getBoard(variant.radius);
  const formation = getFormation(variant);

  const cells: (Piece | null)[] = new Array(board.size).fill(null);
  for (const [k, piece] of Object.entries(formation)) {
    cells[board.indexOf(k)!] = piece;
  }
  return cells;
}
